<script>
  import { afterUpdate } from "svelte";
  import MultiSelect from "svelte-multiselect";
  import { selectedStation, ozoneValues } from ".././store";
  import * as d3 from "d3";

  export let stationsData;

  let limitsExpanded = false;

  function toggleLimits() {
    limitsExpanded = !limitsExpanded;
  }

  // --- Farbskala & Hilfsfunktion -----------------
  const fixedColorScale = d3
    .scaleLinear()
    .domain([0, 40, 80, 120, 160, 200, 240])
    .range(["#2A5589", "#83A8D3", "#95B5DA", "#DE9469", "#B56332", "#913701", "#702E1F"])
    .interpolate(d3.interpolateRgb);

  function getOzoneColor(v) {
    if (v == null || isNaN(v)) return "rgba(200,200,200,.7)";
    if (v > 240) return "#5C1F24";
    return fixedColorScale(v);
  }

  /* --------- NEU: Geokoordinaten- und Distanz-Helper --------- */
  // OpenStreetMap / Nominatim – 1 Ergebnis, JSON zurück
  async function geocode(query) {
    try {
      const url =
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&format=json&limit=1&addressdetails=0&countrycodes=de`;

      const res = await fetch(url, {
        headers: {
          "User-Agent": "OzonKarte/1.0 (contact@example.com)", // Besserer User-Agent
        },
        mode: "cors", // Explizit CORS-Modus
      });

      if (!res.ok) {
        //console.warn(`Geocoding fehlgeschlagen: ${res.status} ${res.statusText}`);
        return null;
      }

      const data = await res.json();
      return data.length ? { lat: +data[0].lat, lon: +data[0].lon } : null;
    } catch (error) {
      //console.warn("Geocoding-Fehler:", error);
      return null;
    }
  }

  // Haversine-Distanz in Kilometern
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  function haversine(lat1, lon1, lat2, lon2) {
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  function nearestStations(coord, n = 5) {
    return stationsData
      .map((s) => ({
        ...s,
        _distance: haversine(coord.lat, coord.lon, s.Lat, s.Long),
      }))
      .sort((a, b) => a._distance - b._distance)
      .slice(0, n);
  }

  async function triggerGeocode(text) {
    const coords = await geocode(text);
    if (!coords) return;

    const nearest = nearestStations(coords, 5);
    if (nearest.length === 0) return;

    const temp = nearest.map((s) => ({
      value: s.Station,
      label: `${s.Stadt} – nächstgelegene Station (${Math.round(s._distance)} km)`,
    }));

    stationOptions = [
      ...temp,
      ...stationsData.map((f) => ({
        value: f.Station,
        label: `${f.Stadt} (${f.PLZ})`,
      })),
    ];

    searchText = ""; // <<<  NEU  – Filter zurücksetzen
    open = true; // Dropdown automatisch öffnen
  }

  let selectedStationDetails = null;
  let stationOptions = [];
  let selectedValue = [];

  let searchText = "";
  let lastSearchText = "";
  let debounceId;

  let matchingOptions = []; // welche Stationen zeigt das Dropdown gerade an?
  let open = false; // Dropdown-Status (MultiSelect bind:open)

  $: {
    const nothingContainsInput = matchingOptions.every(
      (o) => !o.label.toLowerCase().includes(searchText.toLowerCase()),
    );

    if (
      searchText.length >= 4 && // min 4 Zeichen
      nothingContainsInput && // KEIN Treffer mehr
      searchText !== lastSearchText
    ) {
      clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        lastSearchText = searchText;
        triggerGeocode(searchText); // Fallback
      }, 400); // 400 ms Debounce
    }
  }

  $: if (stationsData) {
    stationOptions = stationsData.map((feature) => ({
      value: feature.Station,
      label: feature.Stadt + " (" + feature.PLZ + ")",
    }));
  }

  $: selectedStation.subscribe((stationId) => {
    if (stationId && stationsData) {
      selectedStationDetails = stationsData.find((station) => station.Station === stationId);
      // Hole das passende Option-Objekt für das Select:
      const option = stationOptions.find((opt) => opt.value === stationId);
      selectedValue = option ? [option] : [];
    } else {
      selectedStationDetails = null;
      selectedValue = [];
    }
  });

  function handleMultiSelectChange(selected) {
    if (selected.length > 0) {
      selectedStation.set(selected[0].value);
      //selectedValue = [selected[0]];
    } else {
      selectedStation.set(null);
      selectedValue = [];
    }
  }

  // Zusätzlich: Beim Klick ins Feld das Tag entfernen
  function handleInputFocus() {
    selectedValue = []; // Entfernt das Tag
    // Das Input wird dann wieder sichtbar
  }

  $: $ozoneValues;
  $: $selectedStation;

  $: selectedStationValue = $selectedStation
    ? $ozoneValues.length > 0
      ? $ozoneValues[0][$selectedStation]
      : null
    : null;

  const parseDate = d3.utcParse("%d.%m.%y %H:%M");
  const now = new Date();

  $: selectedStationTimeseries = $selectedStation
    ? $ozoneValues
        .map((row) => {
          const date = parseDate(row.Datum);
          const val = +row[$selectedStation];

          return {
            value: !val || isNaN(val) ? null : val,
            date,
            // alles NACH der echten Gegenwart ist Prognose
            isForecast: date > now,
          };
        })
        .filter((d) => d.value !== null && d.date)
    : [];

  let chartContainer;

  afterUpdate(() => {
    if (selectedStationTimeseries.length > 0 && chartContainer) {
      drawChart();
    }
  });

  function formatDateTime(d) {
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const hours = d.getHours().toString().padStart(2, "0");
    const minutes = d.getMinutes().toString().padStart(2, "0");
    return `${day}.${month}. ${hours}:${minutes}`;
  }

  function formatDateForAxis(d) {
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    return `${day}.${month}.`; // Nur das Datum
  }

  function drawChart() {
    d3.select(chartContainer).selectAll("*").remove();

    // 1. Aktuelle Container-Breite holen
    const containerWidth = chartContainer.clientWidth;
    // 2. Definiere Margins wie gehabt
    const margin = { top: 15, right: 5, bottom: 20, left: 29 };
    // 3. Chart-Breite dynamisch berechnen
    const width = containerWidth - margin.left - margin.right;
    const height = 200 - margin.top - margin.bottom;

    // 4. SVG mit exakter Breite/Höhe (KEIN 100% mehr!)
    const svg = d3
      .select(chartContainer)
      .append("svg")
      .attr("width", containerWidth)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3
      .scaleTime()
      .domain(d3.extent(selectedStationTimeseries, (d) => d.date))
      .range([0, width]);
    const yMax = Math.max(
      120, // Mindestobergrenze
      d3.max(selectedStationTimeseries, (d) => d.value),
    );

    const y = d3.scaleLinear().domain([0, yMax]).nice().range([height, 0]);

    // defs einmal am Anfang der drawChart-Funktion anlegen
    const defs = svg.append("defs");
    let gradIdx = 0;

    // --- Entferne ursprüngliche Achsen und Ticks ---
    svg
      .append("g")
      .call(d3.axisLeft(y).ticks(5))
      .call((g) => g.select(".domain").remove()) // Entfernt die Achsenlinie (Domain)
      .call((g) => g.selectAll(".tick").remove()); // Entfernt die Ticks

    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(4))
      .call((g) => g.select(".domain").remove()) // Entfernt die Achsenlinie (Domain)
      .call((g) => g.selectAll(".tick").remove()); // Entfernt die Ticks

    // --- Gitterlinien ---
    svg
      .append("g")
      .attr("class", "grid")
      .selectAll("line")
      .data(y.ticks(5))
      .enter()
      .append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", (d) => y(d))
      .attr("y2", (d) => y(d));

    // --- Grenzwert 120 µg/m³ ---------------------------------------
    const LIMIT = 120;
    const limitY = y(LIMIT);

    svg
      .append("line")
      .attr("class", "limit-line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", limitY)
      .attr("y2", limitY)
      .attr("stroke", getOzoneColor(LIMIT)) // Farbe aus Ihrer Skala
      .attr("stroke-width", 2.2);
    //.attr("stroke-dasharray", "4,4"); // gestrichelt (optional)

    // optionales Label rechts am Rand
    svg
      .append("text")
      .attr("class", "limit-label")
      .attr("x", width - 2)
      .attr("y", limitY - 4)
      .attr("text-anchor", "end")
      .attr("font-size", 13)
      .attr("fill", getOzoneColor(LIMIT));

    // --- Y-Achse manuell zeichnen ---
    svg.append("line").attr("class", "axis-line y-axis").attr("x1", 0).attr("x2", 0).attr("y1", 0).attr("y2", height);

    svg
      .selectAll(".y-tick")
      .data(y.ticks(5))
      .enter()
      .append("line")
      .attr("class", "tick-line y-tick")
      .attr("x1", -5)
      .attr("x2", 0)
      .attr("y1", (d) => y(d))
      .attr("y2", (d) => y(d));

    svg
      .selectAll(".y-label")
      .data(y.ticks(5))
      .enter()
      .append("text")
      .attr("class", "tick-label y-label")
      .attr("x", -10)
      .attr("y", (d) => y(d) + 3)
      .text((d) => d);

    // --- X-Achse manuell zeichnen ---
    svg
      .append("line")
      .attr("class", "axis-line x-axis")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", height)
      .attr("y2", height);

    svg
      .selectAll(".x-tick")
      .data(x.ticks(4))
      .enter()
      .append("line")
      .attr("class", "tick-line x-tick")
      .attr("x1", (d) => x(d))
      .attr("x2", (d) => x(d))
      .attr("y1", height)
      .attr("y2", height + 5);

    svg
      .selectAll(".x-label")
      .data(x.ticks(4))
      .enter()
      .append("text")
      .attr("class", "tick-label x-label")
      .attr("x", (d) => x(d))
      .attr("y", height + 20)
      .text((d) => formatDateForAxis(d));

    // Messreihe (durchgezogen, Präfix "r")
    const realVals = selectedStationTimeseries.filter((d) => !d.isForecast);
    if (realVals.length > 1) drawColoredLineSeries(realVals, false, "r");

    // Prognose (gestrichelt, Präfix "f")
    const fcVals = selectedStationTimeseries.filter((d) => d.isForecast);
    if (fcVals.length > 1) drawColoredLineSeries(fcVals, true, "f");

    //------------------------------------------------------------------
    // helper: malt jedes Zwischen-Segment mit der Farbe des ersten Punktes
    //------------------------------------------------------------------
    function drawColoredLineSeries(arr, dashed = false, prefix = "r") {
      for (let i = 0; i < arr.length - 1; i++) {
        const p1 = arr[i],
          p2 = arr[i + 1];

        const id = `${prefix}-grad-${gradIdx++}`; // eindeutige ID

        // Gradient definieren (Koordinaten = Chart-Koordinaten)
        defs
          .append("linearGradient")
          .attr("id", id)
          .attr("gradientUnits", "userSpaceOnUse")
          .attr("x1", x(p1.date))
          .attr("y1", y(p1.value))
          .attr("x2", x(p2.date))
          .attr("y2", y(p2.value))
          .call((g) => {
            g.append("stop").attr("offset", "0%").attr("stop-color", getOzoneColor(p1.value));
            g.append("stop").attr("offset", "100%").attr("stop-color", getOzoneColor(p2.value));
          });

        // Segment zeichnen
        svg
          .append("path")
          .datum([p1, p2])
          .attr("fill", "none")
          .attr("stroke", `url(#${id})`)
          .attr("stroke-width", 2.2)
          .attr("stroke-dasharray", dashed ? "6,4" : null)
          .attr(
            "d",
            d3
              .line()
              .x((d) => x(d.date))
              .y((d) => y(d.value)),
          );
      }
    }

    // 1. Finde das erste Prognose-Element
    const forecastStart = selectedStationTimeseries.find((d) => d.isForecast);

    if (forecastStart) {
      const xPos = x(forecastStart.date);
      svg
        .append("line")
        .attr("x1", x(forecastStart.date))
        .attr("x2", x(forecastStart.date))
        .attr("y1", 0)
        .attr("y2", height)
        .attr("stroke", "var(--dv-grey-400)")
        .attr("stroke-width", 0.75);

      // Text „Prognose“ oben rechts an der Linie
      svg
        .append("text")
        .attr("class", "forecast-label")
        .attr("x", xPos + 3)
        .attr("y", 160)
        .text("Prognose");
    }

    const lastRealIdx = selectedStationTimeseries
      .filter((d) => !d.isForecast) // letzter Messwert vor jetzt
      .pop();

    if (lastRealIdx) {
      // Punkt für aktuellen Wert
      svg
        .append("circle")
        .attr("cx", x(lastRealIdx.date))
        .attr("cy", y(lastRealIdx.value))
        .attr("r", 3.5)
        .attr("fill", "var(--dv-black)");

      // Labels in eine Gruppe packen
      const labelGroup = svg.append("g").attr("class", "fixed-labels");

      // Wert-Label (mit weißem Rand)
      labelGroup
        .append("text")
        .attr("x", x(lastRealIdx.date) + 2)
        .attr("y", y(lastRealIdx.value) + 32)
        .attr("font-size", "12px")
        .attr("fill", "var(--dv-black)")
        .attr("stroke", "var(--dv-white)")
        .attr("stroke-width", 3)
        .attr("paint-order", "stroke")
        .attr("text-anchor", "end")
        .style("font-weight", "bold")
        .text(Math.round(lastRealIdx.value));

      // "Aktuell"-Label (mit weißem Rand)
      labelGroup
        .append("text")
        .attr("x", x(lastRealIdx.date) + 2)
        .attr("y", y(lastRealIdx.value) + 18)
        .attr("font-size", "13px")
        .attr("fill", "var(--dv-black)")
        .attr("stroke", "var(--dv-white)")
        .attr("stroke-width", 3)
        .attr("paint-order", "stroke")
        .attr("text-anchor", "end")

        .text("Aktuell");
    }

    // Tooltip-Fokusgruppe
    const focus = svg.append("g").attr("class", "dv-chart-tooltip").style("display", "none");

    focus.append("circle").attr("class", "tooltip-point").attr("r", 4);

    focus.append("text").attr("class", "tooltip-date").attr("x", 10).attr("y", 0).attr("text-anchor", "start");

    focus.append("text").attr("class", "tooltip-value").attr("x", 10).attr("y", 18).attr("text-anchor", "start");

    svg
      .append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .on("mousemove", mousemove)
      .on("mouseenter", () => {
        svg.select(".fixed-labels").style("display", "none");
      })
      .on("mouseleave", () => {
        svg.select(".fixed-labels").style("display", null);
        mouseleave();
      });

    function mousemove(event) {
      const [mx] = d3.pointer(event);
      const bisect = d3.bisector((d) => d.date).left;
      const x0 = x.invert(mx);
      const idx = bisect(selectedStationTimeseries, x0);
      const d0 = selectedStationTimeseries[idx - 1];
      const d1 = selectedStationTimeseries[idx];
      const d = !d0 || (d1 && x0 - d0.date > d1.date - x0) ? d1 : d0;

      if (!d) return;

      focus.style("display", null);
      focus.attr("transform", `translate(${x(d.date)},${y(d.value)})`);

      // Entscheide, ob der Tooltip links oder rechts angezeigt werden soll
      const tooltipWidth = 120; // Schätze die Breite des Tooltips (ggf. anpassen)
      let tooltipOffsetX = 10;
      let textAnchor = "start";
      if (x(d.date) + tooltipWidth > width) {
        // Zu weit rechts: Tooltip nach links zeigen
        tooltipOffsetX = -10;
        textAnchor = "end";
      }

      // Tooltip-Text positionieren und ausrichten
      focus
        .select(".tooltip-date")
        .attr("x", tooltipOffsetX)
        .attr("text-anchor", textAnchor)
        .text(`${d.isForecast ? "Prognose " : ""}${formatDateTime(d.date)}`);

      focus.select(".tooltip-value").attr("x", tooltipOffsetX).attr("text-anchor", textAnchor).text(d.value);
    }

    function mouseleave() {
      focus.style("display", "none");
    }
  }
</script>

<section class="stations-container">
  <MultiSelect
    id="stations"
    maxSelect={1}
    options={stationOptions}
    placeholder="Suchen Sie Ihre Gemeinde/PLZ"
    noMatchingOptionsMsg="Vollständige/n PLZ/Gemeindenamen eingeben."
    bind:selected={selectedValue}
    bind:searchText
    bind:matchingOptions
    bind:open
    on:change={() => handleMultiSelectChange(selectedValue)}
    on:focus={handleInputFocus}
  ></MultiSelect>

  <div id="ozone-wrapper">
    {#if selectedStationDetails}
      <h2>Ozonbelastung in {selectedStationDetails.Stadt}</h2>
      <div class="subline">Ozonwerte der vergangenen drei Tage und Prognose in µg/m³</div>
      <div id="ozone-chart" class="ozone-chart-container" bind:this={chartContainer}></div>
      <p class="details">
        <strong>Standort:</strong>
        {selectedStationDetails.Straße}, {selectedStationDetails.PLZ}
        {selectedStationDetails.Stadt}
      </p>
      <p class="details">
        <strong>Stationstyp:</strong>
        {selectedStationDetails.Art}{selectedStationDetails.Art === "Hintergrund" ? " (Wohngebiet)" : ""}
      </p>
    {:else}
      <p class="no-station-selected">Keine Station ausgewählt.</p>
    {/if}

    <!-- Grenzwerte-Information -->
    <div class="ozone-limits">
      <!-- Mobile: Collapsible Header -->
      <button class="limits-toggle" on:click={toggleLimits}>
        <span>Welche Grenzwerte gelten?</span>
        <div class="chevron-circle" class:expanded={limitsExpanded}>
          <svg viewBox="0 0 24 24" width="8" height="8">
            <path
              d="M8 10l4 4 4-4"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </button>

      <!-- Desktop: Normaler Header (nur auf Desktop sichtbar) -->
      <!-- s<h3 class="desktop-title">Welche Grenzwerte gelten?</h3> -->

      <!-- Content (auf Mobile collapsible) -->
      <div class="limits-content" class:expanded={limitsExpanded}>
        <div class="limits-section">
          <h4>Grenzwerte für Deutschland:</h4>
          <ul>
            <li>120 µg/m³ als 8-Stunden-Mittelwert: Maximal 25 Überschreitungen pro Jahr</li>
            <li>
              180 µg/m³ als stündlicher Mittelwert: Informationsschwelle für besonders empfindliche Bevölkerungsgruppen
            </li>
            <li>240 µg/m³ als stündlicher Mittelwert: Alarmschwelle für Gesamtbevölkerung</li>
          </ul>
        </div>

        <div class="limits-section">
          <h4>WHO-Empfehlungen:</h4>
          <ul>
            <li>60 µg/m³ als 8-Stunden-Mittel in der Peak-Season (April-September)</li>
            <li>100 µg/m³ als 8-Stunden-Mittelwert</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</section>

<style>
</style>
